/// <reference path="../../types/newrelic-video-html5.d.ts" />
"use client";
import * as Html5TrackerModule from "@newrelic/video-html5";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import React, { useCallback, useEffect, useRef } from "react";
import { NEWRELIC_HTML5_VIDEO_CONFIG } from "../../constants/firework";
import { resolveApiVideoMeta, resolveLivestreamStatus } from "../../utils/fireworkVideoMeta";
import {
  buildHtml5TrackerSignature,
  disposeHtml5Tracker,
  findVideoInShadowDOM,
  getHtml5TrackerConstructor,
} from "../../utils/newRelicHtml5Tracker";
import { createFireworkEventHandler, FIREWORK_EVENTS } from "../../utils/newRelicTracking";
import {
  registerVideoPlayerListeners,
  cleanupVideoPlayerListeners,
} from "../../utils/videoplayer/eventListenerManager";
import { createVideoPlayerHandlers } from "../../utils/videoplayer/videoPlayerEventHandlers";
import {
  buildVideoPlayerAnalyticsPayload,
  enrichVideoPlayerCT,
} from "../../utils/livecommerceCleverTap";
interface VideoPlayerProps {
  videoId: string;
  channel?: string;
  className?: string;
  autoplay?: boolean;
  maxVideos?: number;
  playerCaptions?: boolean;
  productCard?: boolean;
  style?: React.CSSProperties;
  enableTracking?: boolean;
  sectionTitle?: string;
  creatorName?: string;
  eventName?: string;
  name?: string;
}
export function VideoPlayer({
  videoId,
  channel = "bajaj_finserv",
  className,
  autoplay = false,
  maxVideos = 1,
  playerCaptions = false,
  productCard = false,
  style,
  enableTracking = true,
  sectionTitle = "Video Player",
  creatorName,
  eventName,
  name,
}: VideoPlayerProps) {
  const fallbackVideoTitle = eventName || videoId || "Video";
  const NR_VIDEO_LOG = "[NR_VIDEO_AGENT]";
  const playerRef = useRef<HTMLElement | null>(null);
  const storyBlockHostRef = useRef<HTMLElement | null>(null);
  const nrTargetRef = useRef<HTMLElement | null>(null);
  const videoTrackerRef = useRef<any>(null);
  const lastTrackedVideoElRef = useRef<HTMLVideoElement | null>(null);
  const trackerInitSignatureRef = useRef<string>("");
  const currentVideoTitleRef = useRef<string>(fallbackVideoTitle);
  const currentVideoIdRef = useRef<string>(videoId || "NA");
  const currentLiveStreamStatusRef = useRef<string>("idle");
  const nrFireworkHandlersRef = useRef<WeakMap<HTMLElement, Record<string, EventListener>>>(
    new WeakMap()
  );
  const handlersRef = useRef<{
    handleVideoStart: (event: Event) => void;
    // handleVideoComplete: (event: Event) => void;
    handleVideoPlay: (event: Event) => void;
    handleVideoPause: (event: Event) => void;
    handleVideoMute: (event: Event) => void;
    handleVideoUnmute: (event: Event) => void;
    handleProductClick: (event: Event) => void;
    handleVideoFullscreen: (event: Event) => void;
    handleVideoImpression: (event: Event) => void;
    handlePlayerMinimize: (_event: Event) => void;
    handlePlayerQuit: (_event: Event) => void;
  } | null>(null);
  const trackingAttachedRef = useRef<WeakSet<HTMLElement>>(new WeakSet());
  const trackFireworkEvent = createFireworkEventHandler("VideoGrid", {
    sectionTitle,
  });

  const resolveVideoTitle = useCallback(
    (eventDetail?: unknown): string => {
      const detail = (eventDetail || {}) as Record<string, unknown>;
      const video = (detail.video || {}) as Record<string, unknown>;

      const resolved =
        (typeof video.caption === "string" && video.caption.trim()) ||
        (typeof video.event_name === "string" && video.event_name.trim()) ||
        (typeof video.title === "string" && video.title.trim()) ||
        (typeof video.description === "string" && video.description.trim()) ||
        (typeof detail.caption === "string" && detail.caption.trim()) ||
        (typeof detail.event_name === "string" && detail.event_name.trim()) ||
        (typeof detail.title === "string" && detail.title.trim()) ||
        fallbackVideoTitle;

      return resolved;
    },
    [fallbackVideoTitle]
  );

  const resolveApiVideoMetaCallback = useCallback(
    (eventDetail?: unknown) => resolveApiVideoMeta({ eventDetail, fallbackVideoId: videoId }),
    [videoId]
  );

  useEffect(() => {
    const apiMeta = resolveApiVideoMetaCallback();
    if (apiMeta.contentTitle) {
      currentVideoTitleRef.current = apiMeta.contentTitle;
    }
    if (apiMeta.videoId) {
      currentVideoIdRef.current = apiMeta.videoId;
    }
    if (apiMeta.liveStreamStatus) {
      currentLiveStreamStatusRef.current = apiMeta.liveStreamStatus;
    }
  }, [resolveApiVideoMetaCallback]);

  const destroyTracker = useCallback(
    (reason: string) => {
      const tracker = videoTrackerRef.current;

      if (!tracker) {
        return;
      }

      disposeHtml5Tracker(
        tracker,
        { log: console.log, error: console.error },
        { reason, logPrefix: NR_VIDEO_LOG }
      );

      videoTrackerRef.current = null;
      lastTrackedVideoElRef.current = null;
      trackerInitSignatureRef.current = "";
    },
    [NR_VIDEO_LOG]
  );

  const shouldTrackNewRelic = useCallback((): boolean => {
    if (typeof window === "undefined") return false;
    const shouldTrack = window.location.pathname.includes("/live");
    return shouldTrack;
  }, []);

  const findStoryBlockForVideo = useCallback((): HTMLElement | null => {
    if (typeof document === "undefined") return null;

    const expectedName = name || `player-${videoId}`;
    const byName = document.querySelector<HTMLElement>(`fw-storyblock[name="${expectedName}"]`);
    if (byName) return byName;

    const byVideo = document.querySelector<HTMLElement>(`fw-storyblock[video="${videoId}"]`);
    if (byVideo) return byVideo;

    return null;
  }, [name, videoId]);

  const setStoryBlockHostRef = useCallback(
    (node: HTMLElement | null) => {
      storyBlockHostRef.current = node;
    },
    [videoId]
  );

  const initVideoTracker = useCallback(
    (fireworkElement: HTMLElement, retries = 15): void => {
      if (typeof window === "undefined" || !shouldTrackNewRelic()) {
        return;
      }

      if (!window.newrelic) {
        if (retries > 0) {
          setTimeout(() => initVideoTracker(fireworkElement, retries - 1), 400);
        }
        return;
      }

      const videoElement = findVideoInShadowDOM(fireworkElement);
      if (!videoElement) {
        if (retries > 0) {
          setTimeout(() => initVideoTracker(fireworkElement, retries - 1), 400);
        }
        return;
      }

      try {
        const apiMeta = resolveApiVideoMetaCallback();
        const resolvedVideoId =
          currentVideoIdRef.current || apiMeta.videoId || videoId || fallbackVideoTitle || "NA";
        const resolvedContentTitle =
          currentVideoTitleRef.current || apiMeta.contentTitle || resolvedVideoId;
        const resolvedLiveStatus =
          currentLiveStreamStatusRef.current || apiMeta.liveStreamStatus || "idle";

        const nextSignature = buildHtml5TrackerSignature({
          videoElement,
          contentTitle: resolvedContentTitle,
          videoId: resolvedVideoId,
          liveStreamStatus: resolvedLiveStatus,
        });

        if (
          videoTrackerRef.current &&
          lastTrackedVideoElRef.current === videoElement &&
          trackerInitSignatureRef.current === nextSignature
        ) {
          return;
        }

        destroyTracker("reinitialize-existing-tracker");

        const Html5TrackerCtor = getHtml5TrackerConstructor(
          Html5TrackerModule as any,
          { log: console.log, error: console.error },
          NR_VIDEO_LOG
        );
        if (!Html5TrackerCtor) return;

        videoTrackerRef.current = new Html5TrackerCtor(videoElement, {
          src: () => videoElement.currentSrc ?? videoElement.src ?? "firework-stream",
          title: () => resolvedContentTitle,
          duration: () => videoElement.duration ?? 0,
          playhead: () => videoElement.currentTime ?? 0,
          customData: {
            contentTitle: resolvedContentTitle,
            videoId: resolvedVideoId,
            liveStreamStatus: resolvedLiveStatus,
          },
          info: NEWRELIC_HTML5_VIDEO_CONFIG,
        });

        lastTrackedVideoElRef.current = videoElement;
        trackerInitSignatureRef.current = nextSignature;
      } catch (error) {
        console.error(NR_VIDEO_LOG, "Html5Tracker initialization failed", {
          error,
          errorMessage: error instanceof Error ? error.message : "unknown",
        });
        // New Relic tracking should not block playback
      }
    },
    [destroyTracker, fallbackVideoTitle, resolveApiVideoMetaCallback, shouldTrackNewRelic, videoId]
  );

  const attachNewRelicListeners = useCallback(
    (target: HTMLElement): void => {
      if (!shouldTrackNewRelic()) return;
      if (nrFireworkHandlersRef.current.has(target)) return;

      const fireworkEventHandlers: Record<string, EventListener> = {};
      const allFireworkEvents = Object.values(FIREWORK_EVENTS).flatMap((group) =>
        typeof group === "object" ? Object.values(group) : []
      );

      for (const eventName of allFireworkEvents) {
        const nrHandler = trackFireworkEvent(eventName) as EventListener;
        const handler: EventListener = (event: Event) => {
          nrHandler(event);
        };

        fireworkEventHandlers[eventName] = handler;
        target.addEventListener(eventName, handler);
      }

      nrFireworkHandlersRef.current.set(target, fireworkEventHandlers);
    },
    [shouldTrackNewRelic, trackFireworkEvent]
  );

  const removeNewRelicListeners = useCallback((target: HTMLElement): void => {
    const fireworkHandlers = nrFireworkHandlersRef.current.get(target);
    if (!fireworkHandlers) return;
    for (const [eventName, handler] of Object.entries(fireworkHandlers)) {
      target.removeEventListener(eventName, handler);
    }
    nrFireworkHandlersRef.current.delete(target);
  }, []);

  useEffect(() => {
    const handlers = createVideoPlayerHandlers({
      sectionTitle,
      videoId,
      creatorName,
      eventName,
      enableTracking,
      NR_VIDEO_LOG,
      resolveVideoTitle,
      resolveApiVideoMetaCallback,
      resolveLivestreamStatus,
      initVideoTracker,
      destroyTracker,
      buildVideoPlayerAnalyticsPayload,
      enrichVideoPlayerCT,
    });

    handlersRef.current = handlers as any;

    return () => {
      handlersRef.current = null;
    };
  }, [
    sectionTitle,
    videoId,
    creatorName,
    eventName,
    enableTracking,
    NR_VIDEO_LOG,
    resolveVideoTitle,
    resolveApiVideoMetaCallback,
    resolveLivestreamStatus,
    initVideoTracker,
    destroyTracker,
    buildVideoPlayerAnalyticsPayload,
    enrichVideoPlayerCT,
  ]);

  const attachEventListeners = useCallback(
    (player: HTMLElement) => {
      if (!handlersRef.current) return;
      if (trackingAttachedRef.current.has(player)) return;

      // Delegate adding fw:* listeners to centralized manager
      registerVideoPlayerListeners(player, handlersRef.current as any, { enableTracking });

      if (shouldTrackNewRelic()) {
        const nrTarget = storyBlockHostRef.current || findStoryBlockForVideo();
        if (nrTarget) {
          nrTargetRef.current = nrTarget;
          attachNewRelicListeners(nrTarget);
          initVideoTracker(nrTarget);
        }
      }

      trackingAttachedRef.current.add(player);
    },
    [
      attachNewRelicListeners,
      enableTracking,
      findStoryBlockForVideo,
      initVideoTracker,
      shouldTrackNewRelic,
    ]
  );

  const removeEventListeners = useCallback(
    (player: HTMLElement) => {
      if (!handlersRef.current) return;
      // Delegate cleanup to centralized manager
      cleanupVideoPlayerListeners(player);
      trackingAttachedRef.current.delete(player);
    },
    [removeNewRelicListeners]
  );

  useEffect(() => {
    if (!shouldTrackNewRelic()) return;

    const hostElement = storyBlockHostRef.current;
    if (!hostElement) return;

    nrTargetRef.current = hostElement;
    attachNewRelicListeners(hostElement);

    return () => {
      removeNewRelicListeners(hostElement);
    };
  }, [attachNewRelicListeners, removeNewRelicListeners, shouldTrackNewRelic]);

  useEffect(() => {
    if (!shouldTrackNewRelic()) return;
    let attempts = 0;
    const maxAttempts = 30;

    const interval = setInterval(() => {
      attempts += 1;

      const storyBlock = findStoryBlockForVideo() || storyBlockHostRef.current;

      if (storyBlock) {
        nrTargetRef.current = storyBlock;
        attachNewRelicListeners(storyBlock);
        initVideoTracker(storyBlock);
        clearInterval(interval);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 250);

    return () => {
      clearInterval(interval);
    };
  }, [
    attachNewRelicListeners,
    findStoryBlockForVideo,
    initVideoTracker,
    shouldTrackNewRelic,
    videoId,
  ]);

  useEffect(() => {
    const host = storyBlockHostRef.current;
    if (host) {
      attachEventListeners(host);
    }

    return () => {
      if (host) {
        removeEventListeners(host);
      }
    };
  }, [attachEventListeners, removeEventListeners, videoId]);

  useEffect(() => {
    let intervalRef: ReturnType<typeof setInterval> | null = null;
    let timeoutRef: ReturnType<typeof setTimeout> | null = null;

    const findPlayer = () => {
      const observer = new MutationObserver(() => {
        const player = document.querySelector(`fw-player[video="${videoId}"]`) as HTMLElement;

        if (player) {
          observer.disconnect();

          if (intervalRef) {
            clearInterval(intervalRef);
            intervalRef = null;
          }

          playerRef.current = player;
          attachEventListeners(player);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      intervalRef = setInterval(() => {
        const player = document.querySelector(`fw-player[video="${videoId}"]`) as HTMLElement;

        if (player) {
          observer.disconnect();

          if (intervalRef) {
            clearInterval(intervalRef);
            intervalRef = null;
          }

          playerRef.current = player;
          attachEventListeners(player);
        }
      }, 100);

      timeoutRef = setTimeout(() => {
        observer.disconnect();
        if (intervalRef) {
          clearInterval(intervalRef);
          intervalRef = null;
        }
      }, 10000);
    };

    findPlayer();

    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      if (playerRef.current) {
        removeEventListeners(playerRef.current);
        playerRef.current = null;
      }

      if (storyBlockHostRef.current) {
        removeEventListeners(storyBlockHostRef.current);
        removeNewRelicListeners(storyBlockHostRef.current);
      }

      if (nrTargetRef.current && nrTargetRef.current !== storyBlockHostRef.current) {
        removeNewRelicListeners(nrTargetRef.current);
      }
      nrTargetRef.current = null;

      destroyTracker("component-unmount");
      currentVideoIdRef.current = videoId || "NA";
      currentLiveStreamStatusRef.current = "idle";
    };
  }, [
    videoId,
    attachEventListeners,
    removeEventListeners,
    removeNewRelicListeners,
    destroyTracker,
  ]);

  return (
    <>
      {React.createElement("fw-storyblock", {
        ref: setStoryBlockHostRef as React.Ref<HTMLElement>,
        "data-testid": "video-player",
        "aria-label": `Video player for video ${videoId}`,
        channel,
        className,
        role: "application",
        "aria-live": "polite",
        video: videoId,
        name: name || `player-${videoId}`,
        tabIndex: 0,
        autoplay: autoplay.toString(),
        player_placement: "bottom-left",
        player_captions: playerCaptions.toString(),
        product_card: productCard.toString(),
        handle_product_card_click: "external",
        livestream_product_card: "fullscreen",
        pip_mode: "soft",
        pip: "true",
        max_videos: "1",
        qr_code: "false",
        email_embed: "false",
        style,
        player_close: "true",
        ui_share_sheet: "copy",
        share_template: "https://www.bajajfinserv.in/live?fw_video=vVqJmb&fw_channel=bajaj_finserv",
        dock: "false",
      })}
    </>
  );
}
